import React, { useState, useRef, useEffect } from 'react';
import { SaveButton, CancelButton } from './CommonButtons';
import { AlertCircle, Upload, X, FileText, Image, Building, Mail, User, CreditCard, MapPin, Package, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import pricingPlanService from '../services/pricingPlanService';

const EditDataroomForm = ({ dataroom, onSave, onCancel }) => {
  const [wizardStep, setWizardStep] = useState(1); // 1: Select Plan, 2: Form Details
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: dataroom?.name || '',
    company_name: dataroom?.company_name || '',
    brand_name: dataroom?.brand_name || '',
    organization_type: dataroom?.organization_type || '',
    registered_office_address: dataroom?.registered_office_address || '',
    industry: dataroom?.industry || '',
    transaction_type: dataroom?.transaction_type || '',
    corporate_identification_no: dataroom?.corporate_identification_no || '',
    pan_number: dataroom?.pan_number || '',
    tan_number: dataroom?.tan_number || '',
    gstin: dataroom?.gstin || '',
    website: dataroom?.website || '',
    primary_contact_name: dataroom?.primary_contact_name || '',
    primary_contact_no: dataroom?.primary_contact_no || '',
    primary_contact_email: dataroom?.primary_contact_email || '',
    secondary_contact_name: dataroom?.secondary_contact_name || '',
    secondary_contact_no: dataroom?.secondary_contact_no || '',
    secondary_contact_email: dataroom?.secondary_contact_email || '',
    organization_email: dataroom?.organization_email || '',
    description: dataroom?.description || '',
    expires_at: dataroom?.expires_at ? dataroom.expires_at.split(' ')[0] : '',
    pricing_plan_id: dataroom?.pricing_plan_id?.toString() || dataroom?.pricing_plan?.id?.toString() || '',
    logo: null // For new logo upload
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(dataroom?.logo_url || dataroom?.logo || null);
  const [isDragging, setIsDragging] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  useEffect(() => {
    // If dataroom has a pricing plan, set it as selected
    if (dataroom?.pricing_plan) {
      console.log('=== EditDataroomForm - Pricing Plan Debug ===');
      console.log('Dataroom pricing_plan:', dataroom.pricing_plan);
      console.log('Plan price:', dataroom.pricing_plan.price);
      console.log('Plan storage:', dataroom.pricing_plan.storage);
      console.log('Plan user_limit:', dataroom.pricing_plan.user_limit);
      setSelectedPlan(dataroom.pricing_plan);
    }
    // Update logo preview when dataroom changes (only if no new logo is selected)
    const logoUrl = dataroom?.logo_url || dataroom?.logo;
    if (logoUrl && !formData.logo) {
      setLogoPreview(logoUrl);
    }
    // Debug logo
    console.log('Dataroom logo_url:', dataroom?.logo_url);
    console.log('Dataroom logo:', dataroom?.logo);
    console.log('Current logoPreview:', logoPreview);
  }, [dataroom]);

  const fetchPricingPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await pricingPlanService.getAvailablePricingPlans();
      setPricingPlans(response.data || response.pricing_plans || []);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setFormData({ ...formData, pricing_plan_id: plan.id.toString() });
  };

  const handleNextStep = () => {
    if (!formData.pricing_plan_id) {
      toast.error('Please select a pricing plan');
      return;
    }
    setWizardStep(2);
  };

  const handlePreviousStep = () => {
    setWizardStep(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Convert email fields to lowercase
    const finalValue = name.includes('email') ? value.toLowerCase() : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processLogoFile(file);
    }
  };

  const processLogoFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo size should be less than 2MB');
      return;
    }

    // Update form data with new file
    setFormData(prev => ({ ...prev, logo: file }));

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processLogoFile(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Dataroom name is required';
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.organization_type.trim()) {
      newErrors.organization_type = 'Organization type is required';
    }

    if (!formData.registered_office_address.trim()) {
      newErrors.registered_office_address = 'Registered office address is required';
    }

    if (!formData.corporate_identification_no.trim()) {
      newErrors.corporate_identification_no = 'Corporate identification no is required';
    }

    if (!formData.pan_number.trim()) {
      newErrors.pan_number = 'PAN number is required';
    }

    if (!formData.tan_number.trim()) {
      newErrors.tan_number = 'TAN number is required';
    }

    if (!formData.gstin.trim()) {
      newErrors.gstin = 'GSTIN is required';
    }

    if (!formData.organization_email.trim()) {
      newErrors.organization_email = 'Organization email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.organization_email)) {
      newErrors.organization_email = 'Invalid email address';
    }

    // Validate website
    if (!formData.website.trim()) {
      newErrors.website = 'Website is required';
    } else if (!/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid URL (e.g., https://example.com)';
    }

    // Validate primary contact person
    if (!formData.primary_contact_name.trim()) {
      newErrors.primary_contact_name = 'Primary contact person name is required';
    }

    if (!formData.primary_contact_no.trim()) {
      newErrors.primary_contact_no = 'Primary contact number is required';
    } else if (!/^[+]?[\d\s-]{10,}$/.test(formData.primary_contact_no)) {
      newErrors.primary_contact_no = 'Please enter a valid phone number';
    }

    if (!formData.primary_contact_email.trim()) {
      newErrors.primary_contact_email = 'Primary contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.primary_contact_email)) {
      newErrors.primary_contact_email = 'Invalid email address';
    }

    // Validate secondary contact person
    if (!formData.secondary_contact_name.trim()) {
      newErrors.secondary_contact_name = 'Secondary contact person name is required';
    }

    if (!formData.secondary_contact_no.trim()) {
      newErrors.secondary_contact_no = 'Secondary contact number is required';
    } else if (!/^[+]?[\d\s-]{10,}$/.test(formData.secondary_contact_no)) {
      newErrors.secondary_contact_no = 'Please enter a valid phone number';
    }

    if (!formData.secondary_contact_email.trim()) {
      newErrors.secondary_contact_email = 'Secondary contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.secondary_contact_email)) {
      newErrors.secondary_contact_email = 'Invalid email address';
    }

    if (!formData.pricing_plan_id) {
      newErrors.pricing_plan_id = 'Please select a pricing plan';
    }

    // if (formData.expires_at) {
    //   const expiryDate = new Date(formData.expires_at);
    //   const today = new Date();
    //   today.setHours(0, 0, 0, 0);

    //   if (expiryDate < today) {
    //     newErrors.expires_at = 'Expiry date cannot be in the past';
    //   }
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full" style={{ maxHeight: 'calc(90vh - 120px)' }}>
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4 mb-4 border-b sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">
              {wizardStep === 1 ? 'Select Subscription Plan' : 'Edit Dataroom'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Step {wizardStep} of 2
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(100% - 60px)' }}>

      {/* Step 1: Select Pricing Plan */}
      {wizardStep === 1 && (
        <div className="space-y-6">
          <div className="mb-6">
            <h4 className="text-lg font-medium mb-2">Choose Your Plan</h4>
            <p className="text-gray-600">Select a subscription plan for your dataroom</p>
          </div>

          {plansLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading pricing plans...</span>
            </div>
          ) : pricingPlans.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No pricing plans available</h3>
              <p className="mt-2 text-gray-500">Please contact support if this issue persists.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => handlePlanSelection(plan)}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    selectedPlan?.id === plan.id
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                    <div className="mt-4 flex items-center justify-center">
                      <span className="text-3xl font-bold text-gray-900">₹{plan.price}</span>
                      <span className="text-gray-600 ml-2">/month</span>
                    </div>
                    <ul className="mt-6 space-y-3 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                        {plan.storage} GB Storage
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                        {plan.user_limit} Users
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                        24/7 Support
                      </li>
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Current Plan Info */}
          {dataroom?.pricing_plan && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Plan</h4>
              <div className="flex items-center gap-3">
                <Package className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-900">{dataroom.pricing_plan.name}</span>
                <span className="text-sm text-gray-500">
                  (₹{dataroom.pricing_plan.price}/mo, {dataroom.pricing_plan.storage} GB)
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <CancelButton onClick={onCancel}>
              Cancel
            </CancelButton>
            <SaveButton onClick={handleNextStep} disabled={!selectedPlan}>
              Next
            </SaveButton>
          </div>
        </div>
      )}

      {/* Step 2: Dataroom Form */}
      {wizardStep === 2 && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Selected Plan Summary */}
          {selectedPlan && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-blue-900">Selected Plan: {selectedPlan.name || 'N/A'}</h5>
                  <p className="text-sm text-blue-700">
                    ₹{selectedPlan.price || '0'}/month • {selectedPlan.storage || '0'} GB Storage • {selectedPlan.user_limit || '0'} Users
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Change Plan
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="col-span-2">
              <h4 className="text-lg font-medium mb-4">Basic Information</h4>
            </div>

            {/* Dataroom Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dataroom Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : ''
                }`}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Company Logo */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="space-y-4">
                {(logoPreview || dataroom) ? (
                  <div className="flex items-start space-x-4 p-5 bg-green-50 rounded-xl border border-green-200 shadow-sm">
                    {logoPreview ? (
                      <div className="relative group">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-24 w-24 rounded-xl object-cover border-2 border-white shadow-lg"
                        />
                      </div>
                    ) : (
                      <div className="h-24 w-24 rounded-xl bg-gray-200 border-2 border-white shadow-lg flex items-center justify-center">
                        <Upload className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex gap-3 mt-3">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5 font-medium rounded-lg text-sm focus:border-transparent"
                        >
                          <Upload className="w-4 h-4" />
                          Change Logo
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <label className={`relative overflow-hidden flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50 shadow-lg scale-[1.02]'
                        : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:shadow-md'
                    }`}>
                      <div className="relative flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className={`w-10 h-10 mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                        <p className="mb-2 text-sm text-gray-700">
                          {isDragging ? (
                            <span className="font-semibold text-blue-600 text-base">Drop your logo here</span>
                          ) : (
                            <>
                              <span className="font-semibold">Click to upload</span>
                              <span className="text-gray-500"> or drag and drop</span>
                            </>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG or GIF (MAX. 2MB)</p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Hidden file input - always present */}
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleLogoUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.company_name ? 'border-red-500' : ''
                }`}
              />
              {errors.company_name && <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>}
            </div>

            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Name
              </label>
              <input
                type="text"
                name="brand_name"
                value={formData.brand_name}
                onChange={handleChange}
                className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
              />
            </div>

            {/* Organization Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization Type <span className="text-red-500">*</span>
              </label>
              <select
                name="organization_type"
                value={formData.organization_type}
                onChange={handleChange}
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.organization_type ? 'border-red-500' : ''
                }`}
              >
                <option value="">Select Organization Type</option>
                <option value="Private Company">Private Company</option>
                <option value="Public Company">Public Company</option>
                <option value="LLP">LLP</option>
                <option value="Partnership Firm">Partnership Firm</option>
                <option value="Sole Proprietorship">Sole Proprietorship</option>
                <option value="Others">Others</option>
              </select>
              {errors.organization_type && <p className="text-red-500 text-sm mt-1">{errors.organization_type}</p>}
            </div>

            {/* Industry */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              <input
                type="text"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
              />
            </div>

            {/* Transaction Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Type
              </label>
              <select
                name="transaction_type"
                value={formData.transaction_type}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select type</option>
                <option value="M&A">M&A</option>
                <option value="IPO">IPO</option>
                <option value="Due Diligence">Due Diligence</option>
                <option value="Fundraising">Fundraising</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Registered Office Address */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registered Office Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="registered_office_address"
                value={formData.registered_office_address}
                onChange={handleChange}
                rows={2}
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.registered_office_address ? 'border-red-500' : ''
                }`}
              />
              {errors.registered_office_address && <p className="text-red-500 text-sm mt-1">{errors.registered_office_address}</p>}
            </div>

            {/* Compliance Details */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-medium mb-4">Compliance Details</h4>
            </div>

            {/* Corporate Identification Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Corporate Identification Number (CIN) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="corporate_identification_no"
                value={formData.corporate_identification_no}
                onChange={handleChange}
                className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
              />
            </div>

            {/* PAN Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PAN Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="pan_number"
                value={formData.pan_number}
                onChange={handleChange}
                className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
              />
            </div>

            {/* TAN Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TAN Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="tan_number"
                value={formData.tan_number}
                onChange={handleChange}
                className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
              />
            </div>

            {/* GSTIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GSTIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
              />
            </div>

            {/* Contact Information */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-medium mb-4">Contact Information</h4>
            </div>

            {/* Website */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.website ? 'border-red-500' : ''
                }`}
              />
              {errors.website && (
                <p className="mt-1 text-sm text-red-500">{errors.website}</p>
              )}
            </div>

            {/* Primary Contact Person Section */}
            <div className="col-span-2 mt-2">
              <h5 className="text-md font-medium text-gray-800">Primary Contact Person</h5>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="primary_contact_name"
                value={formData.primary_contact_name}
                onChange={handleChange}
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.primary_contact_name ? 'border-red-500' : ''
                }`}
              />
              {errors.primary_contact_name && (
                <p className="mt-1 text-sm text-red-500">{errors.primary_contact_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="primary_contact_no"
                value={formData.primary_contact_no}
                onChange={handleChange}
                placeholder="+91 XXXXXXXXXX"
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.primary_contact_no ? 'border-red-500' : ''
                }`}
              />
              {errors.primary_contact_no && (
                <p className="mt-1 text-sm text-red-500">{errors.primary_contact_no}</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email ID <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="primary_contact_email"
                value={formData.primary_contact_email}
                onChange={handleChange}
                placeholder="primary@example.com"
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.primary_contact_email ? 'border-red-500' : ''
                }`}
              />
              {errors.primary_contact_email && (
                <p className="mt-1 text-sm text-red-500">{errors.primary_contact_email}</p>
              )}
            </div>

            {/* Secondary Contact Person Section */}
            <div className="col-span-2 mt-2">
              <h5 className="text-md font-medium text-gray-800">Secondary Contact Person</h5>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="secondary_contact_name"
                value={formData.secondary_contact_name}
                onChange={handleChange}
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.secondary_contact_name ? 'border-red-500' : ''
                }`}
              />
              {errors.secondary_contact_name && (
                <p className="mt-1 text-sm text-red-500">{errors.secondary_contact_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="secondary_contact_no"
                value={formData.secondary_contact_no}
                onChange={handleChange}
                placeholder="+91 XXXXXXXXXX"
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.secondary_contact_no ? 'border-red-500' : ''
                }`}
              />
              {errors.secondary_contact_no && (
                <p className="mt-1 text-sm text-red-500">{errors.secondary_contact_no}</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email ID <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="secondary_contact_email"
                value={formData.secondary_contact_email}
                onChange={handleChange}
                placeholder="secondary@example.com"
                className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.secondary_contact_email ? 'border-red-500' : ''
                }`}
              />
              {errors.secondary_contact_email && (
                <p className="mt-1 text-sm text-red-500">{errors.secondary_contact_email}</p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <CancelButton
              type="button"
              onClick={handlePreviousStep}
            >
              Back
            </CancelButton>
            <div className="flex space-x-3">
              <CancelButton
                type="button"
                onClick={onCancel}
              >
                Cancel
              </CancelButton>
              <SaveButton
                type="submit"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Dataroom'}
              </SaveButton>
            </div>
          </div>
        </form>
      )}
      </div>
    </div>
  );
};

export default EditDataroomForm;